// Client mirror of functions-common/src/charts/engine/index.ts. Keep in sync.

import type {
  ChartMeasure,
  ChartRecordSample,
  ChartResult,
  ChartSeries,
  ChartSeriesPoint,
  ChartSpec,
} from '../types.js'
import {
  CHART_KEY_OTHER,
  CHART_KEY_UNASSIGNED,
  CHART_LABEL_KEY_OTHER,
  CHART_LABEL_KEY_UNASSIGNED,
} from '../limits.js'
import {
  CHART_MAX_COMBO_SERIES_PER_MEASURE,
  CHART_MAX_POINTS_PER_SERIES,
  CHART_MAX_SERIES_PER_CHART,
} from '../limits.js'
import { DATASET_CATALOG } from '../datasets/index.js'
import {
  toDate,
  bucketDate,
  getBucketSequence,
} from './bucket.js'
import {
  filterRows,
  resolveAbsoluteWindow,
  resolveRelativeWindow,
} from './filter.js'
import { groupRows } from './group.js'
import { computeAggregate } from './aggregate.js'
import { limitSeriesCount } from './limitSeries.js'
import { computePreviousPeriod } from './compare.js'
import { sortSeriesPoints } from './sort.js'
import { capSeriesPoints } from './cap.js'

export * from './bucket.js'
export * from './filter.js'
export * from './group.js'
export * from './aggregate.js'
export * from './topN.js'
export * from './limitSeries.js'
export * from './compare.js'
export * from './sort.js'
export * from './cap.js'

export interface EngineContext {
  timeZone?: string
  now?: string | number | Date
}

export function runChartEngine(
  rows: Array<Record<string, unknown>>,
  spec: ChartSpec,
  ctx: EngineContext = {}
): ChartResult {
  const timeZone = ctx.timeZone || 'UTC'
  const nowDate = toDate(ctx.now) ?? new Date()
  const asOf = nowDate.toISOString()

  const datasetDef = DATASET_CATALOG[spec.dataset]
  const effectiveTimeField = spec.time?.field ?? datasetDef?.defaultTimeField

  // 1. Stage 1: Filter
  const filtered = filterRows(
    rows,
    spec.filters ?? [],
    spec.range,
    effectiveTimeField,
    nowDate,
    timeZone
  )

  let droppedSeries = false

  // 2. Build series for a measure
  const buildMeasureSeries = (
    measure: ChartMeasure,
    axis?: 'left' | 'right'
  ): ChartSeries[] => {
    const hasBreakdown = Boolean(spec.breakdown?.field)
    const breakdownGroups = groupRows(filtered, spec.breakdown?.field)
    const resultSeries: ChartSeries[] = []

    for (const [groupKey, groupRowsList] of breakdownGroups.entries()) {
      let label: string
      let labelKey: string | undefined

      if (!hasBreakdown) {
        label = measure.field
      } else if (groupKey === CHART_KEY_UNASSIGNED) {
        label = 'Unassigned'
        labelKey = CHART_LABEL_KEY_UNASSIGNED
      } else if (groupKey === CHART_KEY_OTHER) {
        label = 'Other'
        labelKey = CHART_LABEL_KEY_OTHER
      } else {
        label = groupKey
      }

      // Stage 2: Bucket by time if time dimension present
      if (spec.time?.field && spec.time.bucket) {
        const timeBuckets = new Map<string, Array<Record<string, unknown>>>()

        for (const row of groupRowsList) {
          const rowDate = toDate(row[spec.time.field])
          if (!rowDate) continue
          const bKey = bucketDate(rowDate, spec.time.bucket, timeZone)
          let bList = timeBuckets.get(bKey)
          if (!bList) {
            bList = []
            timeBuckets.set(bKey, bList)
          }
          bList.push(row)
        }

        // F10: Empty time buckets filling
        let orderedBucketKeys: string[] = []
        if (spec.range.kind === 'relative') {
          const rw = resolveRelativeWindow(spec.range.last, spec.range.unit, nowDate, timeZone)
          orderedBucketKeys = getBucketSequence(rw.from, rw.to, spec.time.bucket, timeZone)
        } else if (spec.range.kind === 'absolute') {
          const rw = resolveAbsoluteWindow(spec.range.from, spec.range.to, timeZone)
          if (rw) {
            orderedBucketKeys = getBucketSequence(rw.from, rw.to, spec.time.bucket, timeZone)
          }
        } else {
          // range kind 'all' fills from first to last bucket present
          let minTime = Infinity
          let maxTime = -Infinity
          for (const row of filtered) {
            const rowDate = toDate(row[spec.time.field])
            if (rowDate) {
              const ms = rowDate.getTime()
              if (ms < minTime) minTime = ms
              if (ms > maxTime) maxTime = ms
            }
          }
          if (minTime !== Infinity && maxTime !== -Infinity) {
            orderedBucketKeys = getBucketSequence(
              new Date(minTime),
              new Date(maxTime),
              spec.time.bucket,
              timeZone
            )
          }
        }

        const points: ChartSeriesPoint[] = []

        if (orderedBucketKeys.length > 0) {
          for (const bKey of orderedBucketKeys) {
            const bRows = timeBuckets.get(bKey) ?? []
            const y = bRows.length > 0 ? computeAggregate(bRows, measure) : 0
            points.push({ x: bKey, y })
          }
        } else {
          for (const [bKey, bRows] of timeBuckets.entries()) {
            const y = computeAggregate(bRows, measure)
            points.push({ x: bKey, y })
          }
        }

        resultSeries.push({
          key: groupKey,
          label,
          ...(labelKey ? { labelKey } : {}),
          points,
          ...(axis ? { axis } : {}),
        })
      } else if (hasBreakdown) {
        // Breakdown without time: each breakdown item is a single point on x-axis
        const y = computeAggregate(groupRowsList, measure)
        resultSeries.push({
          key: groupKey,
          label,
          ...(labelKey ? { labelKey } : {}),
          points: [{ x: groupKey, y }],
          ...(axis ? { axis } : {}),
        })
      } else {
        // Neither breakdown nor time: single total point
        const y = computeAggregate(groupRowsList, measure)
        resultSeries.push({
          key: 'total',
          label,
          points: [{ x: label, y }],
          ...(axis ? { axis } : {}),
        })
      }
    }

    return resultSeries
  }

  let series: ChartSeries[]

  if (spec.type === 'combo') {
    let s1 = buildMeasureSeries(spec.measure, 'left')
    const s1Result = limitSeriesCount(s1, {
      limit: CHART_MAX_COMBO_SERIES_PER_MEASURE,
      topN: spec.breakdown?.topN,
      other: spec.breakdown?.other,
    })
    s1 = s1Result.series
    if (s1Result.dropped) droppedSeries = true

    let s2: ChartSeries[] = []
    if (spec.secondMeasure) {
      s2 = buildMeasureSeries(spec.secondMeasure, 'right')
      const s2Result = limitSeriesCount(s2, {
        limit: CHART_MAX_COMBO_SERIES_PER_MEASURE,
        topN: spec.breakdown?.topN,
        other: spec.breakdown?.other,
      })
      if (s2Result.dropped) droppedSeries = true

      // Suffix second measure series keys with :2 and set axis right
      s2 = s2Result.series.map((s) => ({
        ...s,
        key: `${s.key}:2`,
        axis: 'right',
      }))
    }

    series = [...s1, ...s2]
  } else {
    // Single measure: max series per chart
    series = buildMeasureSeries(spec.measure)
    const singleResult = limitSeriesCount(series, {
      limit: CHART_MAX_SERIES_PER_CHART,
      topN: spec.breakdown?.topN,
      other: spec.breakdown?.other,
    })
    series = singleResult.series
    if (singleResult.dropped) droppedSeries = true
  }

  // Stage 6: Compare with previous period
  let previousTotal: number | undefined
  if (spec.compare === 'previousPeriod') {
    previousTotal = computePreviousPeriod(
      rows,
      spec.filters ?? [],
      spec.range,
      effectiveTimeField,
      spec.measure,
      nowDate,
      timeZone
    )
  }

  // Calculate primary total (from filtered directly so total does not double count)
  const primaryTotal = computeAggregate(filtered, spec.measure)

  // Stage 7: Sort
  const isTimeSeries = Boolean(spec.time?.field && spec.time.bucket)
  series = sortSeriesPoints(series, spec.style?.sort, isTimeSeries)

  // Stage 8: Cap at 500 points
  const { series: cappedSeries, truncated: pointsTruncated } = capSeriesPoints(
    series,
    CHART_MAX_POINTS_PER_SERIES
  )
  const isTruncated = droppedSeries || pointsTruncated

  // Calculate target
  let target: number | undefined
  if (spec.target) {
    if (spec.target.kind === 'number') {
      target = spec.target.value
    } else if (spec.target.kind === 'field') {
      target = computeAggregate(filtered, {
        field: spec.target.field,
        agg: 'sum',
      })
    }
  }

  // Build sample records (up to 20), empty string title fallback
  const sample: ChartRecordSample[] = filtered.slice(0, 20).map((r, i) => {
    const rawTitle = r.title ?? r.name ?? r.label
    return {
      id: String(r.id ?? r._id ?? `record-${i}`),
      title: rawTitle != null ? String(rawTitle) : '',
    }
  })

  return {
    series: cappedSeries,
    total: primaryTotal,
    ...(previousTotal != null ? { previousTotal } : {}),
    ...(target != null ? { target } : {}),
    asOf,
    ...(isTruncated ? { truncated: true } : {}),
    records: {
      count: filtered.length,
      sample,
    },
  }
}
